// Lightweight pinch-to-zoom for CoolLightBox images on mobile
// This augments default behavior by scaling the current slide image when pinched.

let teardown = null;

function getScaleFromElement(element) {
  const value = parseFloat(element?.dataset?.pinchScale || '1');
  return Number.isFinite(value) ? value : 1;
}

function setScaleOnElement(element, scale) {
  const clampedScale = Math.max(1, Math.min(scale, 3));
  element.dataset.pinchScale = String(clampedScale);

  // Preserve existing transform if any by appending scale; however, to avoid
  // compounding transforms from library, we only set scale directly here.
  element.style.transformOrigin = 'center center';
  element.style.willChange = 'transform';
  element.style.touchAction = 'none';
  element.style.transform = `scale(${clampedScale})`;
}

function resetScale(element) {
  element.dataset.pinchScale = '1';
  element.style.transform = '';
  element.style.willChange = '';
}

function onMounted() {
  let startDistance = 0;
  let startScale = 1;
  let isPinching = false;
  let activeImg = null;
  let swipeBlockedUntil = 0; // timestamp when swipe events should be unblocked
  const optsStart = { passive: false, capture: true };
  const optsMove = { passive: false, capture: true };
  const optsEnd = { passive: true, capture: true };

  function getActiveImage(target) {
    // Find current slide image within the lightbox
    // Targets can be inside .cool-lightbox__slide__img
    const slide = target.closest('.cool-lightbox__slide');
    if (!slide) return null;
    const img = slide.querySelector('.cool-lightbox__slide__img img, .cool-lightbox__image img, .cool-lightbox__slide img');
    return img || null;
  }

  function distance(t1, t2) {
    const dx = t2.pageX - t1.pageX;
    const dy = t2.pageY - t1.pageY;
    return Math.hypot(dx, dy);
  }

  function handleTouchStart(e) {
    // Block swipe events if we're still in the cooldown period after pinch
    if (e.touches.length === 1 && Date.now() < swipeBlockedUntil) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();
      return;
    }

    if (e.touches.length === 2) {
      activeImg = getActiveImage(e.target);
      if (!activeImg) return;
      isPinching = true;
      startDistance = distance(e.touches[0], e.touches[1]);
      startScale = getScaleFromElement(activeImg);
      // Block lightbox swipe logic during pinch
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();
    }
  }

  function handleTouchMove(e) {
    if (!isPinching || !activeImg) return;
    if (e.touches.length !== 2) return;

    // When zoomed, prevent lightbox swipe/scroll
    if (typeof e.preventDefault === 'function') e.preventDefault();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    else e.stopPropagation();

    const currentDistance = distance(e.touches[0], e.touches[1]);
    const scaleFactor = currentDistance / Math.max(1, startDistance);
    const nextScale = startScale * scaleFactor;
    setScaleOnElement(activeImg, nextScale);
  }

  function handleTouchEnd(e) {
    if (!activeImg) return;

    if (e.touches.length < 2) {
      // End of pinch
      isPinching = false;
      const scaleNow = getScaleFromElement(activeImg);
      if (scaleNow <= 1.02) {
        resetScale(activeImg);
      }
      // Set cooldown period to prevent accidental swipes after pinch
      swipeBlockedUntil = Date.now() + 500; // 500ms cooldown
      // Allow further interactions
      activeImg = null;
    }
    // Prevent the parent lightbox from interpreting this as a swipe end
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    else e.stopPropagation();
  }

  // Delegate on document in capture phase to intercept before lightbox handlers
  document.addEventListener('touchstart', handleTouchStart, optsStart);
  document.addEventListener('touchmove', handleTouchMove, optsMove);
  document.addEventListener('touchend', handleTouchEnd, optsEnd);
  document.addEventListener('touchcancel', handleTouchEnd, optsEnd);

  teardown = () => {
    document.removeEventListener('touchstart', handleTouchStart, optsStart);
    document.removeEventListener('touchmove', handleTouchMove, optsMove);
    document.removeEventListener('touchend', handleTouchEnd, optsEnd);
    document.removeEventListener('touchcancel', handleTouchEnd, optsEnd);
  };
}

export default () => {
  if (process.server) return;
  if (typeof window === 'undefined') return;

  // Install once per page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onMounted, { once: true });
  } else {
    onMounted();
  }

  // Optional: expose cleanup for HMR (Nuxt 2 / Webpack)
  if (typeof module !== 'undefined' && module.hot) {
    module.hot.dispose(function () {
      if (teardown) teardown();
    });
  }
};
